import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ROUTES } from '@/app/config/constants';
import { useAppSelector } from '@/app/store/hooks';
import { selectIsAdmin, selectIsMegaAdmin, selectIsSupplierRole, selectIsSupplierSession } from '@/features/authentication';
import { getLandingRoute } from './landingRoute';

/** A supplier session's own pages: its dashboard (2026-10-29, NB-637) and its BIDs. */
function isSupplierPage(pathname: string): boolean {
  return [ROUTES.SUPPLIER_DASHBOARD, ROUTES.SUPPLIER_BIDS].some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * The shell's index route. Replaces a bare `<Navigate to="/dashboard">` so "/"
 * resolves per role: the dashboard for everyone, Organization Setup for the
 * Mega Admin whose sidebar offers nothing else.
 */
export function RoleLanding() {
  const isMegaAdmin = useAppSelector(selectIsMegaAdmin);
  const isSupplier = useAppSelector(selectIsSupplierSession);
  /* A supplier signed in to the portal starts on its dashboard (2026-10-29, NB-637; its BIDs table before). */
  return <Navigate to={isSupplier ? ROUTES.SUPPLIER_DASHBOARD : getLandingRoute(isMegaAdmin)} replace />;
}

/**
 * The staff dashboard (`/dashboard`): a Supplier-role session -- a supplier signed in to the portal, or a user whose
 * role is Supplier -- is sent to its Supplier Dashboard (2026-10-29, NB-637), as the buyer's BIDs send it to its own.
 * Every other role falls through untouched. The other half is the Supplier feature's `SupplierDashboardGate`.
 */
export function BuyerDashboardGate() {
  const isSupplier = useAppSelector(selectIsSupplierRole);
  return isSupplier ? <Navigate to={ROUTES.SUPPLIER_DASHBOARD} replace /> : <Outlet />;
}

/**
 * Paths a Mega Admin may still open. Everything else inside the shell sends
 * them back to Organization Setup, which is the only screen their sidebar
 * offers and the only one their APIs are wired for.
 *
 * Profile and Settings stay open deliberately: both are reachable from the
 * header chip, which remains on screen, and silently bouncing those links
 * would read as a broken menu rather than as a restriction.
 *
 * The dashboard is NOT among them. A Mega Admin lands on Organization Setup, so
 * allowing the dashboard would only let it reach a screen its sidebar does not
 * list — and keeping it out is what stops DashboardPage mounting at all.
 */
const MEGA_ADMIN_ALLOWED_PREFIXES: readonly string[] = [
  ROUTES.ORGANIZATION_SETUP,
  ROUTES.PROFILE,
  ROUTES.SETTINGS,
];

function isAllowedForMegaAdmin(pathname: string): boolean {
  return MEGA_ADMIN_ALLOWED_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/**
 * Organization Setup is the Mega Admin's screen: it provisions the tenant an
 * Admin then works inside. An Admin's sidebar does not offer it, so this keeps
 * a typed URL consistent with that -- and, more to the point, keeps the
 * wizard's APIs unrequested, which hiding the menu item alone would not.
 */
function isOrganizationSetup(pathname: string): boolean {
  return (
    pathname === ROUTES.ORGANIZATION_SETUP ||
    pathname.startsWith(`${ROUTES.ORGANIZATION_SETUP}/`)
  );
}

/**
 * Pathless layout route wrapping the whole shell. Sits inside <Layout>, so a
 * redirected user keeps the sidebar and header rather than flashing an empty
 * page.
 *
 * Two roles are steered here, in opposite directions: a Mega Admin is held on
 * Organization Setup, and an Admin is kept off it. Doing both in one guard is
 * what keeps the page component from mounting at all -- so neither role fetches
 * the other's APIs, and no chunk is loaded for a screen that will not render.
 *
 * Every other role falls through untouched.
 */
export function RoleShellGuard() {
  const isMegaAdmin = useAppSelector(selectIsMegaAdmin);
  const isAdmin = useAppSelector(selectIsAdmin);
  const isSupplier = useAppSelector(selectIsSupplierSession);
  const { pathname } = useLocation();

  /*
    A supplier has only its dashboard and its BIDs (2026-10-29, NB-637): "/" is its dashboard, and any staff page sends
    it back to its BIDs table (2026-10-27).
  */
  if (isSupplier && !isSupplierPage(pathname)) {
    return <Navigate to={pathname === ROUTES.ROOT ? ROUTES.SUPPLIER_DASHBOARD : ROUTES.SUPPLIER_BIDS} replace />;
  }

  if (isMegaAdmin && !isAllowedForMegaAdmin(pathname)) {
    return <Navigate to={ROUTES.ORGANIZATION_SETUP} replace />;
  }

  if (isAdmin && isOrganizationSetup(pathname)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <Outlet />;
}
